import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Upload, Loader2, Plus, FileUp, CheckCircle, XCircle } from "lucide-react";

export const ReferenceImageUpload = ({
  newRefImage,
  setNewRefImage,
  refImageInputRef,
  uploadingNewRef,
  onFileSelect,
  onAddImage,
  onBulkUpload
}) => {
  const [bulkLabel, setBulkLabel] = useState("reference");
  const [bulkPositive, setBulkPositive] = useState(true);
  const [bulkDescription, setBulkDescription] = useState("");

  return (
    <div className="space-y-6" data-testid="ref-upload-section">
      {/* Single Upload */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Add Single Reference Image</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Label *</Label>
              <Input
                placeholder="e.g., Correct sensor alignment"
                value={newRefImage.label}
                onChange={(e) => setNewRefImage({ ...newRefImage, label: e.target.value })}
                data-testid="ref-label-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Example Type</Label>
              <div className="flex items-center space-x-2 h-10">
                <Switch
                  checked={newRefImage.is_positive}
                  onCheckedChange={(checked) => setNewRefImage({ ...newRefImage, is_positive: checked })}
                  data-testid="ref-positive-toggle"
                />
                <span className={newRefImage.is_positive ? "text-green-600" : "text-red-600"}>
                  {newRefImage.is_positive ? (
                    <span className="flex items-center"><CheckCircle className="w-4 h-4 mr-1" /> Positive</span>
                  ) : (
                    <span className="flex items-center"><XCircle className="w-4 h-4 mr-1" /> Negative</span>
                  )}
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              placeholder="Describe what makes this a good/bad example..."
              value={newRefImage.description}
              onChange={(e) => setNewRefImage({ ...newRefImage, description: e.target.value })}
              rows={2}
              data-testid="ref-description-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Image</Label>
            <div
              className="aspect-video max-w-md bg-muted rounded-lg border-2 border-dashed flex items-center justify-center cursor-pointer hover:bg-muted/80 transition-colors"
              onClick={() => refImageInputRef.current?.click()}
            >
              {newRefImage.image_base64 ? (
                <img
                  src={`data:image/jpeg;base64,${newRefImage.image_base64}`}
                  alt="Selected"
                  className="w-full h-full object-contain rounded-lg"
                />
              ) : (
                <div className="text-center text-muted-foreground">
                  <Upload className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Click to select image</p>
                </div>
              )}
            </div>
            <input
              ref={refImageInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={onFileSelect}
              data-testid="ref-file-input"
            />
          </div>

          <Button
            onClick={onAddImage}
            disabled={uploadingNewRef || !newRefImage.label || !newRefImage.image_base64}
            data-testid="add-ref-btn"
          >
            {uploadingNewRef ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Add Reference Image
          </Button>
        </CardContent>
      </Card>

      {/* Bulk Upload */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Bulk Upload</CardTitle>
          <CardDescription>Upload multiple images at once with the same label and type</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Label Prefix</Label>
              <Input
                placeholder="e.g., correct_wiring"
                value={bulkLabel}
                onChange={(e) => setBulkLabel(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Example Type</Label>
              <div className="flex items-center space-x-2 h-10">
                <Switch
                  checked={bulkPositive}
                  onCheckedChange={setBulkPositive}
                />
                <span className={bulkPositive ? "text-green-600" : "text-red-600"}>
                  {bulkPositive ? (
                    <span className="flex items-center"><CheckCircle className="w-4 h-4 mr-1" /> Positive (correct)</span>
                  ) : (
                    <span className="flex items-center"><XCircle className="w-4 h-4 mr-1" /> Negative (defect)</span>
                  )}
                </span>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Description (applies to all)</Label>
            <Input
              placeholder="e.g., Correctly seated terminal block with proper torque"
              value={bulkDescription}
              onChange={(e) => setBulkDescription(e.target.value)}
            />
          </div>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            className="hidden"
            id="bulk-upload"
            onChange={(e) => onBulkUpload(e, bulkLabel, bulkPositive, bulkDescription)}
            data-testid="bulk-upload-input"
          />
          <Button
            variant="outline"
            onClick={() => document.getElementById("bulk-upload").click()}
            disabled={uploadingNewRef}
            data-testid="bulk-upload-btn"
          >
            {uploadingNewRef ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileUp className="w-4 h-4 mr-2" />}
            Select Multiple Images
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
